import { useQuery } from '@tanstack/react-query';
import { Eye, Share2, Quote, CheckCircle, Clock } from 'lucide-react';
import { api } from '@/lib/api';
import { StatsCard } from '@/components/admin/StatsCard';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';

interface Stats {
  totalQuotes: number;
  publishedQuotes: number;
  totalViews: number;
  totalShares: number;
  todayQuote: {
    id: number;
    text: string;
    author: string;
    category: string;
  } | null;
  recentActivity: Array<{
    id: number;
    action: string;
    quoteText: string;
    timestamp: string;
  }>;
}

export function DashboardPage() {
  const { data: stats, isLoading, error } = useQuery<Stats>({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const res: any = await api.admin.getStats();
      return res.data;
    },
  });

  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-red-500">Failed to load dashboard stats</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-surface-900">Dashboard</h1>
        <p className="text-surface-500 mt-1">Overview of your quote platform</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total Quotes"
          value={stats?.totalQuotes ?? 0}
          icon={<Quote size={24} />}
          loading={isLoading}
        />
        <StatsCard
          title="Published"
          value={stats?.publishedQuotes ?? 0}
          icon={<CheckCircle size={24} />}
          loading={isLoading}
        />
        <StatsCard
          title="Total Views"
          value={stats?.totalViews?.toLocaleString() ?? '0'}
          icon={<Eye size={24} />}
          change="+12% this week"
          changeType="positive"
          loading={isLoading}
        />
        <StatsCard
          title="Total Shares"
          value={stats?.totalShares?.toLocaleString() ?? '0'}
          icon={<Share2 size={24} />}
          change="+8% this week"
          changeType="positive"
          loading={isLoading}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card variant="glass" className="p-6">
          <h2 className="text-lg font-bold text-surface-900 mb-4">Today's Quote</h2>
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ) : stats?.todayQuote ? (
            <blockquote className="border-l-4 border-primary-500 pl-4 py-2">
              <p className="text-lg text-surface-800 italic">"{stats.todayQuote.text}"</p>
              <footer className="mt-3 text-sm text-surface-500">
                — {stats.todayQuote.author} · {stats.todayQuote.category}
              </footer>
            </blockquote>
          ) : (
            <p className="text-surface-500 italic">No quote set for today</p>
          )}
        </Card>

        <Card variant="glass" className="p-6">
          <h2 className="text-lg font-bold text-surface-900 mb-4">Recent Activity</h2>
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <div className="flex-1 space-y-1">
                    <Skeleton className="h-3 w-3/4" />
                    <Skeleton className="h-3 w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : stats?.recentActivity?.length ? (
            <div className="space-y-3">
              {stats.recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start gap-3 p-3 rounded-xl hover:bg-surface-50 transition-colors">
                  <div className="p-2 rounded-full bg-surface-100 text-surface-600 mt-0.5">
                    <Clock size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-surface-800 truncate">{activity.action}</p>
                    <p className="text-xs text-surface-500 truncate">{activity.quoteText}</p>
                  </div>
                  <span className="text-xs text-surface-400 whitespace-nowrap">
                    {new Date(activity.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-surface-500">No recent activity</p>
          )}
        </Card>
      </div>
    </div>
  );
}
